import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import FAQAccordion from './FAQAccordion';
import Seo from '../Seo';

const heroImage = '/award-logos/pd1.png';
const secondaryImage = '/award-logos/pd2.png';
const fallbackImage = '/services_detail_tech_1773741342619.png';

const capabilities = [
  {
    title: 'Modern Design Systems',
    desc: 'Engineer scalable, component-based design languages (Atomicity) that ensure visual consistency and rapid UI iteration across complex digital portfolios.'
  },
  {
    title: 'Full-Stack Engineering',
    desc: 'Develop high-performance architectures using distributed microservices, reactive frontends, and resilient backend clusters that scale to millions of users.'
  },
  {
    title: 'Legacy Modernization',
    desc: 'Phased modernization of monolithic legacy systems into cloud-native microservices using Strangler Fig patterns to ensure zero business disruption.'
  },
  {
    title: 'Quality Engineering (QE)',
    desc: 'Implement automated CI/CD gates, shift-left testing models, and continuous security scanning to ensure software is production-ready at all times.'
  },
  {
    title: 'Mobile Ecosystems',
    desc: 'Native and cross-platform mobile development (iOS, Android) focused on high-fidelity performance, offline resilience, and seamless API integration.'
  },
  {
    title: 'API-First Architecture',
    desc: 'Design and implement robust GraphQL and RESTful API layers that power multi-channel ecosystems with security, rate-limiting, and documentation.'
  }
];



const useCases = [
  { title: 'SaaS Platform Acceleration', desc: 'Scaling B2B platforms from MVP to enterprise-grade via multi-tenant architecture and robust security frameworks.' },
  { title: 'Digital Marketplace Hubs', desc: 'Building complex high-concurrency platforms with integrated payments, logistics, and real-time inventory.' },
  { title: 'Internal Enterprise Tools', desc: 'Developing custom workflow automation and data-visualization tools to drive internal operational efficiency.' },
  { title: 'Omni-Channel Portals', desc: 'Creating unified customer experiences across web, mobile, and kiosk interfaces with a single backend source of truth.' }
];

const modules = [
  {
    title: 'Ideation & UX Discovery',
    points: ['Design thinking workshops', 'Interactive wireframing', 'User behavioral research']
  },
  {
    title: 'High-Velocity Engineering',
    points: ['Microservices (Node/Java/Go)', 'React/Next.js/Vue ecosystems', 'Cloud-native orchestration']
  },
  {
    title: 'Continuous Quality (QE)',
    points: ['End-to-end test automation', 'Load & stress validation', 'Vulnerability assessments']
  },
  {
    title: 'Sustenance & Evolution',
    points: ['24/7 SRE monitoring', 'Feature backlog evolution', 'Technical debt management']
  }
];

const techStack = [
  { title: 'Frontend & UI', items: ['React', 'Next.js', 'Typescript', 'Tailwind CSS', 'Redux/Zustand'] },
  { title: 'Backend & APIs', items: ['Node.js', 'Java Spring Boot', 'Python Fast API', 'Go', 'GraphQL'] },
  { title: 'Database / Caching', items: ['PostgreSQL', 'MongoDB', 'Redis', 'Elasticsearch', 'DynamoDB'] },
  { title: 'Mobile & DevOps', items: ['React Native', 'Flutter', 'Kubernetes', 'AWS', 'GitHub Actions'] }
];

const executionSteps = [
  { phase: 'Discovery & UX Strategy', desc: 'Perform deep-dive stakeholder alignment and user research to define a roadmap that balances business goals with user needs.' },
  { phase: 'Technical Architecture', desc: 'Design scalable, multi-tenant skeletons and infrastructure-as-code foundations to ensure long-term stability.' },
  { phase: 'Agile Sprint Implementation', desc: 'Execute bi-weekly sprints focusing on high-value feature release, supported by automated testing and continuous review.' },
  { phase: 'QA & Security Hardening', desc: 'Rigorous validation of all functional requirements, edge cases, and security perimeters before transition to production.' },
  { phase: 'UAT & Deployment', desc: 'Collaborative user acceptance testing followed by automated blue/green or canary deployments to ensure zero-downtime launches.' },
  { phase: 'Monitoring & Optimization', desc: 'Transition to active SRE monitoring and continuous enhancement cycles based on real-world user metrics and feedback.' }
];

const industries = ['Banking & Fintech', 'Healthcare', 'Enterprise SaaS', 'Retail', 'Logistics', 'Energy'];

const faqs = [
  {
    q: 'How does Libsys approach the build-vs-buy decision for enterprises?',
    a: 'We guide our clients through a rigorous feasibility analysis. We recommend "Buy" for non-differentiating utility functions (like email or basic CRM) and "Build" for core IP that provides a competitive advantage. Our engineering team focuses on creating modular applications that easily integrate with third-party SaaS via robust API layers, ensuring you maintain control over your most valuable business logic while leveraging off-the-shelf efficiency where it makes sense.'
  },
  {
    q: 'What is your methodology for managing technical debt in long-term projects?',
    a: 'Technical debt is inevitable but must be managed. We dedicate a fixed percentage of every sprint (typically 15-20%) to refactoring, library updates, and documentation. We use automated static analysis tools to monitor debt levels in real-time. This "pay-as-you-go" approach prevents the accumulation of structural rot that would otherwise slow down your delivery velocity and increase maintenance costs over the product lifecycle.'
  },
  {
    q: 'How do you handle rapid changes in requirements during the development lifecycle?',
    a: 'We embrace Agile change through disciplined Product Management. Our bi-weekly sprint reviews provide a natural checkpoint to re-prioritize the backlog based on new market data or stakeholder feedback. We use "Feature Flags" to decouple deployment from release, allowing us to keep developing the core while toggling specific functionalities on or off as requirements evolve, ensuring the engineering engine never stalls due to shifting goals.'
  },
  {
    q: 'Can you scale development teams (Agile Pods) quickly to meet project peaks?',
    a: 'Absolutely. We utilize a "Structured Pod" model where each unit is a self-contained team of developers, QA engineers, and a scrum master. We have a robust internal talent pipeline that allows us to spin up additional pods within 2-4 weeks. Each pod operates within the same architectural governance and CI/CD standards, ensuring that adding more people actually results in increased velocity rather than just increased management overhead.'
  },
  {
    q: 'What is your approach to ensuring high-fidelity User Experience (UX)?',
    a: 'UX is not just about aesthetics; it\'s about performance and usability. We start every project with a "Design Discovery" phase where we build interactive prototypes to test user flows early. We then develop a durable "Design System" in Figma that translates directly into code components. This ensures that the professional vision of the designers is perfectly reflected in the final engineering product, resulting in a premium feel that drives user adoption and satisfaction.'
  },
  {
    q: 'How do you ensure the products you build are ready for enterprise-scale traffic?',
    a: 'We architect for scale from Day 1. This includes implementing horizontal scaling (Kubernetes/Auto-scaling), aggressive caching strategies (Redis/CDN), and asynchronous processing for heavy workloads. Before any major launch, we perform rigorous "Stress & Load Testing" to identify the exact breaking point of the system and optimize the database queries and network configurations accordingly, ensuring your application remains responsive even during peak demand cycles.'
  }
];

const ServiceProductDevelopment = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white min-h-screen pt-8 [&_.text-h1]:text-h2 [&_.text-h2]:text-h3 [&_.text-body]:text-small [&_.text-small]:text-subtext">
      <Seo
        title="Product Development Services"
        description="Libsys product engineering — MVP prototyping, UX/UI design systems, agile delivery. Strategic product development from ideation to scale with enterprise-grade reliability."
        path="/services/product-development"
      />
      {/* ══ HERO SECTION ══════════════════════════════════════════════════════ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-12">
        <div className="rounded-[2.5rem] border border-slate-200 bg-slate-50/70 p-6 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 text-strategic-tagline mb-5">
              Product Engineering & Innovation
            </span>
            <h1 className="text-h1 text-primary mb-5">
              Engineer High-Performance Digital Products at Scale
            </h1>
              <p className="text-slate-600 text-body leading-[1.7] font-medium mb-6 max-w-2xl">
                Libsys delivers end-to-end digital engineering services that transform bold ideas into market-leading software. We combine rigorous design-thinking with high-fidelity engineering to build scalable, resilient, and user-centric applications. Whether you are launching a disruptive MVP or modernizing a legacy enterprise ecosystem, our agile engineering pods ensure your digital products are production-ready, highly portable, and built for continuous growth. By integrating automated CI/CD gates and shift-left testing models, we ensure that every release meets the highest standards of reliability and security.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 mb-8">
                {[
                  'Full-Lifecycle Product Engineering',
                  'Distributed Microservices Architecture',
                  'Modern Web & Mobile Ecosystems',
                  'Strategic UX/UI Design Systems',
                  'Legacy Tech Modernization',
                  'Automated Quality Engineering (QE)'
                ].map((item) => (
                  <li key={item} className="text-slate-700 text-subtext font-bold leading-relaxed flex items-start gap-2">
                    <span className="w-2 h-2 rounded-full bg-secondary mt-[7px] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-4">
                <a
                  href="tel:+16307991556"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-primary hover:bg-[#254b85] text-white font-black text-[11px] uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-blue-900/10 active:scale-95 group"
                >
                  Request Project Consultation
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="lg:col-span-7 lg:-mt-12">
            <div className="rounded-[2.5rem] overflow-hidden border border-slate-200 shadow-[0_30px_70px_-15px_rgba(35,96,134,0.2)] bg-slate-100 relative group">
              <div className="absolute inset-0 bg-primary/5 group-hover:bg-transparent transition-colors z-10 pointer-events-none" />
              <img src={heroImage} alt="Product Design Simulation" className="w-full h-[400px] lg:h-[600px] object-cover group-hover:scale-105 transition-transform duration-1000" onError={(e) => { e.currentTarget.src = fallbackImage; }} />
            </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ CORE CAPABILITIES ════════════════════════════════════════════════ */}
      <section className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-14 text-center">
            <span className="text-strategic-tagline block mb-2">Technical Depth</span>
            <h2 className="text-h2 text-primary">Advanced Product Engineering Capabilities</h2>
            <p className="text-slate-600 text-body max-w-3xl mx-auto mt-4 leading-relaxed">
              Our engineering practice combines high-precision development with design-led innovation, ensuring that software performs reliably in high-stakes enterprise environments.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {capabilities.map((item) => (
              <div key={item.title} className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <h3 className="text-[22px] font-black text-primary mb-4 leading-tight">{item.title}</h3>
                <p className="text-slate-600 text-subtext leading-relaxed font-medium">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ SERVICE PORTFOLIO ════════════════════════════════════════════════ */}
      <section className="py-16 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="rounded-[2.5rem] border border-slate-200 bg-slate-50/70 overflow-hidden shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-stretch">
            <div className="lg:col-span-8 p-8 lg:p-12">
              <span className="text-strategic-tagline block mb-3">Service Portfolio</span>
              <h2 className="text-h2 text-primary mb-6">Strategic Product Development Modules</h2>
              <p className="text-slate-600 text-body mb-8 max-w-3xl">
                We deliver end-to-end product services with clear roadmap ownership so project goals can scale from initial discovery to unified global deployment.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {modules.map((module) => (
                  <div key={module.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h3 className="text-body font-black text-primary mb-4 flex items-center gap-2">
                       {module.title}
                    </h3>
                    <ul className="space-y-3">
                      {module.points.map((point) => (
                        <li key={point} className="text-slate-600 text-subtext font-semibold leading-relaxed flex items-start gap-2.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-2 flex-shrink-0" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-4 border-t lg:border-t-0 lg:border-l border-slate-200 bg-slate-100 relative overflow-hidden">
               <img src={secondaryImage} alt="Product Engineering Lifecycle" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.src = fallbackImage; }} />
               <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent flex flex-col justify-end p-8">
                  <p className="text-white text-lg font-black leading-tight italic">"Bridging the gap between conceptual design and high-availability production code."</p>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ DELIVERY PHASES ══════════════════════════════════════════════════ */}
      <section className="py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-12 mb-10">
              <span className="text-strategic-tagline block mb-3">Implementation Framework</span>
              <h2 className="text-h2 text-primary mb-6">A Disciplined Path to Modern Software Success</h2>
              <p className="text-slate-600 text-body max-w-4xl">
                 Success in modern product development requires more than just coding; it requires a structured lifecycle that manages feature risk, quality, and stakeholder alignment in lockstep.
              </p>
            </div>
            <div className="lg:col-span-12">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-10">
                {executionSteps.map((step, idx) => (
                  <div key={step.phase} className="relative pl-12">
                    <div className="absolute left-0 top-0 text-5xl font-black text-slate-200 leading-none">
                      {(idx + 1).toString().padStart(2, '0')}
                    </div>
                    <div className="relative">
                      <h4 className="text-body font-black text-primary mb-2 uppercase tracking-wide">{step.phase}</h4>
                      <p className="text-slate-600 text-subtext leading-relaxed font-medium">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ TECH STACK & USE CASES ════════════════════════════════════════════ */}
      <section className="py-16 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-12">
             <span className="text-strategic-tagline block mb-3 text-center lg:text-left">Technology Ecosystem</span>
             <h2 className="text-h2 text-primary mb-6 text-center lg:text-left">Modern Full-Stack Engineering Toolsets</h2>
             <p className="text-slate-600 text-body mb-10 max-w-4xl text-center lg:text-left mx-auto lg:mx-0">
                We leverage a specialized toolset that connects proven enterprise environments with the latest in cloud-native innovation to ensure your product is resilient, portable, and secure. Our engineering standards prioritize horizontal scalability and clean structural integrity.
             </p>
             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {techStack.map(cat => (
                  <div key={cat.title} className="p-5 rounded-2xl border border-slate-200 bg-slate-50/50 hover:bg-white hover:shadow-lg transition-all">
                     <h5 className="font-black text-primary text-tiny uppercase tracking-widest mb-3">{cat.title}</h5>
                     <div className="flex flex-wrap gap-2">
                        {cat.items.map(i => (
                          <span key={i} className="px-2.5 py-1 bg-white border border-slate-200 rounded-md text-tiny font-bold text-slate-600">{i}</span>
                        ))}
                     </div>
                  </div>
                ))}
             </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch pt-12 border-t border-slate-100">
             <div className="space-y-6">
                <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm h-full">
                   <h3 className="text-xl font-black text-primary mb-6">Strategic Use-Cases</h3>
                   <div className="space-y-6">
                      {useCases.map(uc => (
                         <div key={uc.title} className="border-b border-slate-100 pb-5 last:border-b-0 last:pb-0">
                            <h4 className="text-primary font-black text-body uppercase tracking-wide mb-1">{uc.title}</h4>
                            <p className="text-slate-500 text-body font-medium leading-relaxed">{uc.desc}</p>
                         </div>
                      ))}
                   </div>
                </div>
             </div>

             <div className="space-y-6">
                <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
                   <h3 className="text-xl font-black text-primary mb-6">Engineering Maturity Benchmarks</h3>
                   <div className="space-y-4">
                      {[
                        'Automated CI/CD pipelines with zero-downtime canary releases.',
                        'Design system implementation for site-wide visual consistency.',
                        'Micro-frontend architectures for complex enterprise portals.',
                        'Robust API-first foundations for multi-channel ecosystems.'
                      ].map(b => (
                        <div key={b} className="flex items-start gap-4">
                           <div className="w-2 h-2 rounded-full bg-secondary mt-2.5 flex-shrink-0" />
                           <p className="text-slate-600 text-body font-medium leading-relaxed">{b}</p>
                        </div>
                      ))}
                   </div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-3xl p-8">
                   <h3 className="text-[17px] font-black text-primary mb-4">Target Sectors</h3>
                   <div className="flex flex-wrap gap-2">
                     {industries.map(ind => (
                       <span key={ind} className="px-3 py-1.5 bg-white border border-slate-200 rounded-xl text-subtext font-bold text-slate-600 shadow-sm">{ind}</span>
                     ))}
                   </div>
                </div>
             </div>
          </div>
      </section>

      <FAQAccordion faqs={faqs} />
    </div>
  );
};

export default ServiceProductDevelopment;
